import { useEffect, useState } from "react";

import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import {
  TransactionStateType,
  useTransactionsDispatch,
  useTransactionState,
} from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { logError } from "@hl/shared-features/lib/services/logger";
import { Box, Button, Modal, Text } from "@mantine/core";

import { _CollectionType, TransactionType } from "apollo/graphql.generated";

/* eslint-disable  @typescript-eslint/no-explicit-any */
type Props = {
  collectionId: string;
  address: string;
  isOpened: boolean;
  onClose: () => void;
  mirroredBaseUri: string;
  collectionType: _CollectionType;
  chainId: number;
};

export const SetBaseUriModal = ({
  isOpened,
  onClose,
  collectionId,
  address,
  mirroredBaseUri,
  collectionType,
  chainId,
}: Props) => {
  const { walletAddress } = useAuth();
  const getNewTxnId = () => `txn-${Date.now()}`;
  const [error, setError] = useState<string>("");
  const [txnId, setTxnId] = useState<string>(getNewTxnId());

  const transactionDispatch = useTransactionsDispatch();
  const transactionState = useTransactionState(txnId);

  const onSubmit = async () => {
    setError("");

    if (!collectionId) {
      throw new Error("Collection without id");
    }

    try {
      if (walletAddress) {
        const txnId = getNewTxnId();

        const params = [mirroredBaseUri];

        const contractData = {
          address: address,
          args: JSON.stringify(params),
          chainId,
          method: "setBaseURI",
          value: "0",
          bytecode: null,
        };

        transactionDispatch?.({
          type: "START_TRANSACTION",
          payload: {
            id: txnId,
            entityId: collectionId,
            transactionType: TransactionType.EVM_SET_BASE_URI,
            collectionType: collectionType,
            args: contractData,
            metadata: {
              collectionId,
              nftContractAddress: walletAddress,
            },
          },
        });
        setTxnId(txnId);
      }
    } catch (e) {
      setError("Failed to save changes.");
      logError({
        e,
        message: "[EditRoyaltiesModal] Failed to update",
      });
    }
  };

  let buttonLabel = "Decentralize metadata on contract";
  let buttonDisabled = false;
  if (transactionState && !transactionState.error && !!transactionState.args) {
    if (transactionState.type === TransactionStateType.WaitingSignedTx) {
      buttonLabel = "Waiting for user signature...";
      buttonDisabled = true;
    } else if (transactionState.type !== TransactionStateType.Done) {
      buttonLabel = "Updating ...";
      buttonDisabled = true;
    }
  }

  useEffect(() => {
    if (transactionState?.type === TransactionStateType.Done) {
      onClose();
    }
  }, [transactionState?.type]);

  return (
    <Modal
      size="lg"
      opened={isOpened}
      onClose={onClose}
      title="Decentralize metadata on contract"
    >
      <Box mx="auto">
        <Text size="sm">
          This will add your final metadata directory, stored on Arweave, to
          your smart contract. We highly recommend doing this once your mint is
          complete. If you do it before your mint is done, tokens revealed after
          will not have metadata showing. You&apos;ll need to do it again to
          show the metadata of minted tokens.
        </Text>
        <ModalSection>
          <Button fullWidth onClick={onSubmit} disabled={buttonDisabled}>
            {buttonLabel}
          </Button>
          {error && (
            <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
              {error}
            </Text>
          )}
          <Text size="xs" align="center" mt={8}>
            This action requires a transaction
          </Text>
        </ModalSection>
      </Box>
    </Modal>
  );
};
